import { create } from 'zustand'
import type {
  FlipperContextItem,
  FlipperSessionContext
} from '@dolfin/business/flipper/session-context'

export type {
  FlipperContextItem,
  FlipperSessionContext
} from '@dolfin/business/flipper/session-context'

const STORAGE_KEY_OPEN = 'flipper-chat-open'
const STORAGE_KEY_MAXIMIZED = 'flipper-chat-maximized'
const STORAGE_KEY_CONVERSATION = 'flipper-chat-conversation-id'

function readBool(key: string, fallback = false): boolean {
  if (typeof window === 'undefined') return fallback
  return localStorage.getItem(key) === 'true'
}

function readString(key: string): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(key) || null
}

function dedupeItems(sources: Record<string, FlipperSessionContext | null>): FlipperContextItem[] {
  const all = Object.values(sources).flatMap(source =>
    source?.kind === 'entity-refs' ? source.refs : []
  )

  return Array.from(new Map(all.map(i => [`${i.type}:${i.id}`, i])).values())
}

function getSessionContext(
  sources: Record<string, FlipperSessionContext | null>
): FlipperSessionContext[] {
  return Object.values(sources).filter((source): source is FlipperSessionContext => source != null)
}

type FlipperChatState = {
  isOpen: boolean
  isMaximized: boolean
  selectedConversationId: string | null
  pendingMessage: string | null
  pendingDraftMessage: string | null
  pendingSessionContext: FlipperSessionContext[] | null
  activeSessionContextOverride: FlipperSessionContext[] | null
  _sessionContextSources: Record<string, FlipperSessionContext | null>
  contextItems: FlipperContextItem[]
  sessionContext: FlipperSessionContext[]
  pendingContextRefs: FlipperContextItem[] | null
  open: () => void
  close: () => void
  toggle: () => void
  toggleMaximize: () => void
  setSelectedConversationId: (id: string | null) => void
  openWithMessage: (
    message: string,
    contextRefs?: FlipperContextItem[],
    sessionContextOverride?: FlipperSessionContext[]
  ) => void
  openWithDraft: (
    message: string,
    contextRefs?: FlipperContextItem[],
    sessionContextOverride?: FlipperSessionContext[]
  ) => void
  consumePendingMessage: () => string | null
  consumePendingDraftMessage: () => string | null
  consumePendingContextRefs: () => FlipperContextItem[] | null
  consumePendingSessionContext: () => FlipperSessionContext[] | null
  setActiveSessionContextOverride: (context: FlipperSessionContext[] | null) => void
  setSessionContext: (sourceKey: string, context: FlipperSessionContext | null) => void
  clearSessionContext: (sourceKey: string) => void
}

export const useFlipperChatState = create<FlipperChatState>((set, get) => ({
  isOpen: readBool(STORAGE_KEY_OPEN),
  isMaximized: readBool(STORAGE_KEY_MAXIMIZED),
  selectedConversationId: readString(STORAGE_KEY_CONVERSATION),
  pendingMessage: null,
  pendingDraftMessage: null,
  pendingSessionContext: null,
  activeSessionContextOverride: null,
  _sessionContextSources: {},
  contextItems: [],
  sessionContext: [],
  pendingContextRefs: null,
  open: () => {
    localStorage.setItem(STORAGE_KEY_OPEN, 'true')
    set({ isOpen: true })
  },
  close: () => {
    localStorage.setItem(STORAGE_KEY_OPEN, 'false')
    set({ isOpen: false })
  },
  toggle: () =>
    set(state => {
      const next = !state.isOpen
      localStorage.setItem(STORAGE_KEY_OPEN, String(next))
      return { isOpen: next }
    }),
  toggleMaximize: () =>
    set(state => {
      const next = !state.isMaximized
      localStorage.setItem(STORAGE_KEY_MAXIMIZED, String(next))
      return { isMaximized: next }
    }),
  setSelectedConversationId: (id: string | null) => {
    if (id) {
      localStorage.setItem(STORAGE_KEY_CONVERSATION, id)
    } else {
      localStorage.removeItem(STORAGE_KEY_CONVERSATION)
    }
    set({ selectedConversationId: id })
  },
  openWithMessage: (
    message: string,
    contextRefs?: FlipperContextItem[],
    sessionContextOverride?: FlipperSessionContext[]
  ) => {
    localStorage.setItem(STORAGE_KEY_OPEN, 'true')
    localStorage.removeItem(STORAGE_KEY_CONVERSATION)
    set({
      pendingMessage: message,
      pendingDraftMessage: null,
      pendingContextRefs: contextRefs ?? null,
      pendingSessionContext: sessionContextOverride ?? null,
      activeSessionContextOverride: sessionContextOverride ?? null,
      selectedConversationId: null,
      isOpen: true
    })
  },
  openWithDraft: (
    message: string,
    contextRefs?: FlipperContextItem[],
    sessionContextOverride?: FlipperSessionContext[]
  ) => {
    localStorage.setItem(STORAGE_KEY_OPEN, 'true')
    localStorage.removeItem(STORAGE_KEY_CONVERSATION)
    set({
      pendingMessage: null,
      pendingDraftMessage: message,
      pendingContextRefs: contextRefs ?? null,
      pendingSessionContext: sessionContextOverride ?? null,
      activeSessionContextOverride: sessionContextOverride ?? null,
      selectedConversationId: null,
      isOpen: true
    })
  },
  consumePendingMessage: () => {
    const msg = get().pendingMessage
    if (msg) set({ pendingMessage: null })
    return msg
  },
  consumePendingDraftMessage: () => {
    const msg = get().pendingDraftMessage
    if (msg) set({ pendingDraftMessage: null })
    return msg
  },
  consumePendingContextRefs: () => {
    const refs = get().pendingContextRefs
    if (refs) set({ pendingContextRefs: null })
    return refs
  },
  consumePendingSessionContext: () => {
    const context = get().pendingSessionContext
    if (context) set({ pendingSessionContext: null })
    return context
  },
  setActiveSessionContextOverride: context => {
    set({ activeSessionContextOverride: context })
  },
  setSessionContext: (sourceKey: string, context: FlipperSessionContext | null) => {
    set(state => {
      const sources = { ...state._sessionContextSources, [sourceKey]: context }
      return {
        _sessionContextSources: sources,
        contextItems: dedupeItems(sources),
        sessionContext: getSessionContext(sources)
      }
    })
  },
  clearSessionContext: (sourceKey: string) => {
    set(state => {
      const sources = { ...state._sessionContextSources }
      delete sources[sourceKey]
      return {
        _sessionContextSources: sources,
        contextItems: dedupeItems(sources),
        sessionContext: getSessionContext(sources)
      }
    })
  }
}))
